const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Get stored token
const getToken = () => localStorage.getItem('token');

// Make authenticated API request
const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Handle network errors (server unreachable)
  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (networkError) {
    throw new Error(`Network error: ${networkError.message || 'Unable to connect to the server. Please check your connection and try again.'}`);
  }

  // Handle 401 - clear token and redirect to auth
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/auth';
    throw new Error('Session expired. Please sign in again.');
  }

  // Handle non-JSON responses (e.g. nginx 502/503 HTML error pages)
  let data;
  try {
    data = await response.json();
  } catch (parseError) {
    if (response.status === 502) {
      throw new Error('Server is temporarily unavailable. Please try again in a moment.');
    }
    if (response.status === 503) {
      throw new Error('Service temporarily unavailable. Please try again shortly.');
    }
    if (response.status === 413) {
      throw new Error('Request data is too large. Please shorten your input and try again.');
    }
    if (response.status === 504) {
      throw new Error('Request timed out. Please try again.');
    }
    throw new Error(`Unexpected server response (${response.status}). Please try again.`);
  }

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Request failed');
  }

  return data;
};

// Auth API
export const auth = {
  register: (data) => apiRequest('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  login: (data) => apiRequest('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  adminLogin: (data) => apiRequest('/auth/admin-access-7x9k2m', { method: 'POST', body: JSON.stringify(data) }),
  verifyEmail: (token) => apiRequest(`/auth/verify-email?token=${token}`),
  forgotPassword: (data) => apiRequest('/auth/forgot-password', { method: 'POST', body: JSON.stringify(data) }),
  resetPassword: (data) => apiRequest('/auth/reset-password', { method: 'POST', body: JSON.stringify(data) }),
  resendVerification: (data) => apiRequest('/auth/resend-verification', { method: 'POST', body: JSON.stringify(data) }),
  getMe: () => apiRequest('/auth/me'),
};

// Reports API
export const reports = {
  create: (data) => apiRequest('/reports/create', { method: 'POST', body: JSON.stringify(data) }),
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/reports${query ? `?${query}` : ''}`);
  },
  get: (id) => apiRequest(`/reports/${id}`),
  getStatus: (id) => apiRequest(`/reports/${id}/status`),
  download: async (id, format = 'docx') => {
    const token = getToken();
    const response = await fetch(`${API_URL}/reports/${id}/download?format=${format}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  },
  bulkDownload: async (reportIds) => {
    const token = getToken();
    const response = await fetch(`${API_URL}/reports/bulk-download`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ reportIds })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Bulk download failed');
    }
    return response.blob();
  },
  deleteReport: (id) => apiRequest(`/reports/${id}`, { method: 'DELETE' }),
  bulkDelete: (reportIds) => apiRequest('/reports/bulk-delete', {
    method: 'DELETE',
    body: JSON.stringify({ reportIds })
  }),
  submitReview: (id, data) => apiRequest(`/reports/${id}/review`, { method: 'POST', body: JSON.stringify(data) }),
  updateAdminAccess: (id, action) => apiRequest(`/reports/${id}/admin-access`, {
    method: 'POST',
    body: JSON.stringify({ action })
  }),
};

// Stripe API
export const stripe = {
  createCheckout: (plan, voucherCode) => {
    if (voucherCode) {
      return apiRequest('/stripe/create-checkout-with-voucher', { method: 'POST', body: JSON.stringify({ plan, voucherCode }) });
    }
    return apiRequest('/stripe/create-checkout', { method: 'POST', body: JSON.stringify({ plan }) });
  },
  validateVoucher: (code, plan) => apiRequest('/stripe/validate-voucher', { method: 'POST', body: JSON.stringify({ code, plan }) }),
  getPortal: () => apiRequest('/stripe/portal'),
  getPlans: () => apiRequest('/stripe/plans'),
};

// User API
export const user = {
  getProfile: () => apiRequest('/user/profile'),
  updateProfile: (data) => apiRequest('/user/profile', { method: 'PUT', body: JSON.stringify(data) }),
  changePassword: (data) => apiRequest('/user/change-password', { method: 'POST', body: JSON.stringify(data) }),
  exportData: () => apiRequest('/user/data-export', { method: 'POST' }),
  deleteAccount: (data) => apiRequest('/user/delete-account', { method: 'POST', body: JSON.stringify(data) }),
  cancelDeletion: () => apiRequest('/user/cancel-deletion', { method: 'POST' }),
  getSubscription: () => apiRequest('/user/subscription'),
};

// Contact API (public, includes Data Subject Request endpoint)
export const contact = {
  enterprise: (data) => apiRequest('/contact/enterprise', { method: 'POST', body: JSON.stringify(data) }),
  dataRequest: (data) => apiRequest('/contact/data-request', { method: 'POST', body: JSON.stringify(data) }),
};

// Admin API
export const admin = {
  getStats: () => apiRequest('/admin/stats'),
  getReports: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin/reports${query ? `?${query}` : ''}`);
  },
  getReport: (id) => apiRequest(`/admin/reports/${id}`),
  deleteReport: (id) => apiRequest(`/admin/reports/${id}`, { method: 'DELETE' }),
  cancelReport: (id) => apiRequest(`/admin/reports/${id}/cancel`, { method: 'POST' }),
  retryReport: (id) => apiRequest(`/admin/reports/${id}/retry`, { method: 'POST' }),
  bulkDeleteReports: (reportIds) => apiRequest('/admin/reports/bulk', {
    method: 'DELETE',
    body: JSON.stringify({ reportIds })
  }),
  downloadReport: async (id, format = 'docx') => {
    const token = getToken();
    const response = await fetch(`${API_URL}/admin/reports/${id}/download?format=${format}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  },
  getUsers: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin/users${query ? `?${query}` : ''}`);
  },
  getUser: (id) => apiRequest(`/admin/users/${id}`),
  updateUser: (id, data) => apiRequest(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getConfig: () => apiRequest('/admin/config'),
  getAISettings: () => apiRequest('/admin/config/ai-settings'),
  updateConfig: (data) => apiRequest('/admin/config', { method: 'PUT', body: JSON.stringify(data) }),
  updateDefaultModel: (model) => apiRequest('/admin/config/default-model', { method: 'PUT', body: JSON.stringify({ model }) }),
  updateAgentModels: (data) => apiRequest('/admin/config/agent-models', { method: 'PUT', body: JSON.stringify(data) }),
  testProvider: (provider) => apiRequest('/admin/config/test-provider', { method: 'POST', body: JSON.stringify({ provider }) }),
  updateApiKeys: (data) => apiRequest('/admin/config/api-keys', { method: 'PUT', body: JSON.stringify(data) }),
  updateFallbackChain: (data) => apiRequest('/admin/config/fallback-chain', { method: 'PUT', body: JSON.stringify(data) }),
  refreshModels: () => apiRequest('/admin/config/refresh-models', { method: 'POST' }),
  refreshBedrockModels: (region) => apiRequest('/admin/config/refresh-bedrock-models', { method: 'POST', body: JSON.stringify({ region }) }),
  getModelUpdaterHistory: () => apiRequest('/admin/config/model-updater-history'),

  // Country-specific enrichment connectors (admin smoke test only;
  // each connector is feature-flag gated and not yet wired into reports).
  enrichmentIreland: (query) => apiRequest('/admin/enrichment/ireland', { method: 'POST', body: JSON.stringify({ query }) }),
  enrichmentFrance: (query) => apiRequest('/admin/enrichment/france', { method: 'POST', body: JSON.stringify({ query }) }),
  enrichmentPortugal: (query) => apiRequest('/admin/enrichment/portugal', { method: 'POST', body: JSON.stringify({ query }) }),
  // Free-data global enrichment connectors (Module B — admin smoke test only,
  // single `enrichmentGlobal` flag, not yet wired into the live pipeline).
  globalSanctions: (query) => apiRequest('/admin/global-enrichment/opensanctions', { method: 'POST', body: JSON.stringify({ query }) }),
  globalGleif: (query) => apiRequest('/admin/global-enrichment/gleif', { method: 'POST', body: JSON.stringify({ query }) }),
  globalOpencorporates: (query) => apiRequest('/admin/global-enrichment/opencorporates', { method: 'POST', body: JSON.stringify({ query }) }),
  globalGdelt: (query) => apiRequest('/admin/global-enrichment/gdelt', { method: 'POST', body: JSON.stringify({ query }) }),
  globalOfac: (query) => apiRequest('/admin/global-enrichment/ofac', { method: 'POST', body: JSON.stringify({ query }) }),
  globalOfsi: (query) => apiRequest('/admin/global-enrichment/ofsi', { method: 'POST', body: JSON.stringify({ query }) }),
  globalEdgar: (query) => apiRequest('/admin/global-enrichment/edgar', { method: 'POST', body: JSON.stringify({ query }) }),
  getFeatures: () => apiRequest('/admin/features'),
  updateFeatures: (updates) => apiRequest('/admin/features', { method: 'PATCH', body: JSON.stringify(updates) }),
  verifyReport: (id) => apiRequest(`/admin/reports/${id}/verify`),
  getSolanaStatus: () => apiRequest('/admin/solana/status'),
  // API Keys (Module E Tier 1)
  listApiKeys: () => apiRequest('/admin/api-keys'),
  createApiKey: (data) => apiRequest('/admin/api-keys', { method: 'POST', body: JSON.stringify(data) }),
  updateApiKey: (id, data) => apiRequest(`/admin/api-keys/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  topupApiKey: (id, amount) => apiRequest(`/admin/api-keys/${id}/topup`, { method: 'POST', body: JSON.stringify({ amount }) }),
  dryRunApiKey: (id, requiredCredits = 1) => apiRequest(`/admin/api-keys/${id}/dry-run`, { method: 'POST', body: JSON.stringify({ requiredCredits }) }),
  deleteApiKey: (id) => apiRequest(`/admin/api-keys/${id}`, { method: 'DELETE' }),
  bulkDownload: async (reportIds) => {
    const token = getToken();
    const response = await fetch(`${API_URL}/admin/reports/bulk-download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ reportIds })
    });
    if (!response.ok) throw new Error('Bulk download failed');
    return response.blob();
  },
  getAnalytics: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin/analytics${query ? `?${query}` : ''}`);
  },
  getAuditLog: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin/audit-log${query ? `?${query}` : ''}`);
  },
  // Vouchers
  getVouchers: () => apiRequest('/admin/vouchers'),
  createVoucher: (data) => apiRequest('/admin/vouchers', { method: 'POST', body: JSON.stringify(data) }),
  updateVoucher: (id, data) => apiRequest(`/admin/vouchers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteVoucher: (id) => apiRequest(`/admin/vouchers/${id}`, { method: 'DELETE' }),
  sendVoucherEmail: (id, email) => apiRequest(`/admin/vouchers/${id}/send-email`, { method: 'POST', body: JSON.stringify({ email }) }),
  // Reviews
  getReviews: () => apiRequest('/admin/reviews'),
  deleteReview: (id) => apiRequest(`/admin/reviews/${id}`, { method: 'DELETE' }),
};

export default { auth, reports, stripe, user, contact, admin };
